/**
 * Loads the tracker once and keeps it patched in place.
 *
 * Mutations return the item as it was written, so the list is updated locally rather than
 * refetched -- the same convention the rest of db-admin uses (see ReferralCodes).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { hipaaService } from '../services/hipaaService';
import type {
  HipaaActivityEntry,
  HipaaItem,
  HipaaItemPatch,
} from '../types/hipaaTypes';
import { computeStats } from '../utils/hipaaStats';

export const useHipaaData = () => {
  const [items, setItems] = useState<HipaaItem[]>([]);
  const [activity, setActivity] = useState<HipaaActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await hipaaService.getChecklist();
      setItems(result.items);
      setActivity(result.activity);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load the HIPAA checklist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateItem = useCallback(async (itemId: string, patch: HipaaItemPatch) => {
    const updated = await hipaaService.updateItem(itemId, patch);
    setItems((previous) => previous.map((item) => (item.id === itemId ? updated : item)));
    return updated;
  }, []);

  const addComment = useCallback(async (itemId: string, text: string) => {
    const result = await hipaaService.addComment(itemId, text);
    setItems((previous) =>
      previous.map((item) =>
        item.id === itemId ? { ...item, commentCount: result.commentCount } : item,
      ),
    );
    return result.comment;
  }, []);

  const stats = useMemo(() => computeStats(items), [items]);

  return { items, activity, stats, loading, error, reload: load, updateItem, addComment };
};

export type HipaaData = ReturnType<typeof useHipaaData>;
